/**
 * Common page functions.
 *
 * @module app/pages/PageCommon
 */
import _ from 'lodash';
import { getFeatureParamsFromCookie, isClientFeatureEnabled } from '@wh/feature-toggle-detector';
import docCookies from 'doc-cookies';
import BindingComponents from '../components/BindingComponents';
import IntegrationGlobals from '../integration/globals';
import jQuery from 'jquery';
import LazyLoadingViewAdapter from '../views/LazyLoadingViewAdapter';
import MessageBusTopics from '../../common/MessageBusTopics';
import OddsFormatProvider from '../components/OddsFormatProvider';
import PreferencesProvider from '../components/PreferencesProvider';
import BettingTVChannelsProvider from '../components/BettingTVChannelsProvider';
import Router from '../controllers/Router';
import DomPushAdapter from '../components/push/domUpdates/DomPushAdapter';
import PushAdapter from '../components/push/PushAdapter';
import Translator from '../components/Translator';
import DeviceDetector from '../components/DeviceDetector';
import { validateVersion } from '../../utils';
import SportsDataRepository from '../components/SportsDataRepository';
import { hidePagePreloader, showPagePreloader } from '../libs/PagePreloaderHelper';

/**
 * css selectors used to identify elements
 * @type {{CAROUSEL_ICON_ELEMENT: string, CAROUSEL_IMAGE_ELEMENT: string, CONTEXTUAL_NAV_IMAGE: string,
     *          CONTEXTUAL_NAV_TITLE: string, LEFT_NAV_LINK: string}}
 * @private
 */
const SELECTORS = {
    CAROUSEL_ICON_ELEMENT: '#carousel li.contextual-nav__item:not("contextual-nav__item-sprite"):first',
    CAROUSEL_IMAGE_ELEMENT: '#carousel li.contextual-nav__item-sprite:first',
    CONTEXTUAL_NAV_IMAGE: '.contextual-nav__img',
    CONTEXTUAL_NAV_TITLE: '.contextual-nav__title',
    LEFT_NAV_LINK: 'li:not(#nav-homepage):first',
};

/**
 * Adds a new element to the mobile carousel
 * @param {String}  topic   The messagebus topic triggering this action
 * @param {Object}  payload The icon and position data for the new element
 * @private
 */
const addCarouselElement = (topic, payload) => {
    if (hasRequiredCarouselParams(payload)) {
        const { position, iconName = '' } = payload;
        const createElement = (iconName.length > 0) ? createCarouselIconElement : createCarouselImageElement;
        const $newButton = createElement(payload);
        $newButton.insertBefore(`#carousel li:nth-child(${position})`);
    }
};

/**
 * Reducer used to validate object keys
 * @param {Object}  payload     MessageBus add item payload
 * @param {Boolean} acc         Accumulator for the reducer
 * @param {String}  requiredKey A required key
 * @return  {Boolean}           All keys checked so far are present
 * @private
 */
const hasKeysReducer = (payload, acc, requiredKey) => acc && payload.hasOwnProperty(requiredKey);

/**
 * Checks whether a payload contains required keys
 * @param {Object}  payload     MessageBus add item payload
 * @return  {Boolean}           Payload contains all required keys
 * @private
 */
const hasRequiredCarouselParams = (payload) =>
    ['position', 'targetUrl', 'label'].reduce(hasKeysReducer.bind(null, payload), true);

/**
 * Creates a new carousel element with a background image
 * @param {String}  imageUrl    The url for the background image
 * @param {String}  targetUrl   The url the link should direct to
 * @param {String}  label       The text to display under the button
 * @return {Object}             jQuery object representing the new link
 * @private
 */
const createCarouselImageElement = ({ imageUrl, targetUrl, label }) => {
    const $baseButton = jQuery(SELECTORS.CAROUSEL_IMAGE_ELEMENT).clone();
    const $anchor = $baseButton.find('a');
    ['desktop-url', 'mobile-url', 'embedded-url', 'data-trk-attached']
        .forEach((attr) => $anchor.removeAttr(attr));
    $anchor.attr('href', targetUrl);
    $baseButton.find(SELECTORS.CONTEXTUAL_NAV_IMAGE).attr('style', `background-image:url('${imageUrl}')`);
    $baseButton.find(SELECTORS.CONTEXTUAL_NAV_TITLE).text(label);
    return $baseButton;
};

/**
 * Creates a new carousel element with an icon
 * @param {String}  iconName    The name of the icon in the icon font pack
 * @param {String}  targetUrl   The url that the link should point to
 * @param {String}  label       The text to display under the button
 * @return {Object}             jQuery object representing the new link
 * @private
 */
const createCarouselIconElement = ({ iconName = '', targetUrl, label }) => {
    const $baseButton = jQuery(SELECTORS.CAROUSEL_ICON_ELEMENT).clone();
    $baseButton.find('a').attr('href', targetUrl);
    $baseButton.find('div').attr('id', `nav-${label.toLowerCase()}`);
    $baseButton.find('i').attr('class', `contextual-nav__icon icon-${iconName.toLowerCase()}`);
    $baseButton.find(SELECTORS.CONTEXTUAL_NAV_TITLE).text(label);
    return $baseButton;
};

/**
 * Adds a new element to the desktop left nav
 * @param {String}  topic   The messagebus topic triggering this action
 * @param {Object}  payload The icon and position data for the new element
 * @private
 */
const addLeftNavElement = (topic, payload) => {
    if (hasRequiredLeftNavParams(payload)) {
        const { position, area } = payload;
        const $container = jQuery(`#desktop-sidebar-${area}`);
        const $newLink = createLeftNavLink($container, payload);
        $newLink.insertBefore(`${$container.selector} li:nth-child(${position})`);
    }
};

/**
 * Checks whether a payload contains required keys
 * @param {Object}  payload     MessageBus add item payload
 * @return  {Boolean}           Payload contains all required keys
 * @private
 */
const hasRequiredLeftNavParams = (payload) =>
    ['position', 'iconName', 'targetUrl', 'label', 'area'].reduce(hasKeysReducer.bind(null, payload), true);

/**
 * Creates a new left nav link element
 * @param {Object}  $container  jQuery element representing the container for the new link
 * @param {String}  iconName    Name for the icon in the icon font pack
 * @param {String}  targetUrl   The url that this link should direct to
 * @param {String}  label       The text to display on the link
 * @returns {Object}            jQuery object representing the new link
 * @private
 */
const createLeftNavLink = ($container, { iconName, targetUrl, label }) => {
    const $baseLink = $container.find(SELECTORS.LEFT_NAV_LINK).clone();
    $baseLink.attr('id', `nav-${label.toLowerCase()}`);
    $baseLink.attr('data-en-id', `nav-${label.toLowerCase()}`);
    $baseLink.find('a').attr('href', targetUrl);
    $baseLink.find('a').attr('title', label);
    $baseLink.find('a').html(`<span class="icon-${iconName.toLowerCase()}"></span> ${label}`);
    return $baseLink;
};

/**
 * Check if is proper native app version for New Betting TV streaming (Betting TV-2 & Quantum Leap)
 * @returns {Boolean} Ne Betting TV is supported in native app
 * @private
 */
const isNativeSupportForNewBettingTv = () => {
    const appVersion = DeviceDetector.getAppVersion();
    if (DeviceDetector.isIOS()) {
        return validateVersion(appVersion, { min: '11.0', max: '*' });
    }
    if (DeviceDetector.isAndroid()) {
        return validateVersion(appVersion, { min: '2.15.1', max: '*' });
    }
    return false;
};

/**
 * Global PubSub reference
 *
 * @type {Object}
 *
 * @private
 */
var pubSub = window.WH.messageBus;

/**
 * @var {Number} Current URL debounce value used to prevent ajax out-of-sync updates on multi click
 */
var debouncer = 0;

/**
 * @var {Object} Root DOM element.
 */
var content = null;

/**
 * Function to add a custom domain for ajax requests
 * if available
 *
 * @param {String} url      Ajax URL
 * @param {Object} WHSports WH.sportbook object
 *
 * @private
 */
var addAjaxDomain = function (url, WHSports) {

    if (typeof WHSports.ajaxDomain !== undefined) {
        return WHSports.ajaxDomain + url;
    }

    return url;
};

/**
 * Function to add the cdn domain for ajax requests which should go through staticcache
 *
 * @param {String} url The route part of a url
 * @param {Object} WHSports The window.WH.sportsbook object
 *
 * @returns {String} the complete url
 *
 * @private
 */
var addCdnAjaxDomain = function (url, WHSports) {

    if (
        WHSports.frontEndConfig &&
        WHSports.frontEndConfig.fragmentCdnHost
    ) {

        return WHSports.frontEndConfig.fragmentCdnHost + url;
    }

    return url;
};

/**
 * Checks whether to use staticcache or the regular ajax domain
 *
 * @param {String} url The route part of a url
 * @param {Object} WHSports The window.WH.sportsbook object
 *
 * @returns {String} the complete url
 *
 * @private
 */
var addDomain = function addDomain(url, WHSports, useCdn) {

    if (useCdn && WHSports.frontEndConfig.useFragmentCdn) {

        return addCdnAjaxDomain(url, WHSports);
    }

    return addAjaxDomain(url, WHSports);
};

/**
 * Make a URL suitable for internal navigation.
 * In other words, if someone tries to use a URL of the form:
 * http://sports.williamhill.com/betting/something
 * When we try to route that, things will go wrong.
 * So, we strip off the domain and protocol.
 *
 * @param {String} href URL to normalise.
 * @returns {String}
 * @private
 */
var normaliseHref = function (href) {

    var expression = new RegExp('https?:\/\/[^\/]*\/betting', 'gi');

    if (typeof href === 'string' && href.match(expression)) {
        return href.replace(expression, '/betting');
    } else {
        return href;
    }
};

/**
 * Get a normalised href either from the provided parameter or
 * by quering the href attribute in the current scope.
 *
 * @param {String} providedHref Optional href.
 * @returns {String}
 * @private
 */
var getNormalisedHref = function (providedHref) {

    if (providedHref && typeof providedHref === 'string') {
        return normaliseHref.call(this, providedHref);
    } else {
        return jQuery(this).attr('href');
    }
};

/**
 * Checking if should debounce
 *
 * @param {Boolean} notDebounce Flag which indicates if debouncing is on
 * @param {Number} debounceNum Debounce counter
 *
 * @returns {Boolean}
 *
 * @private
 */
var ifDebounce = function ifDebounce(notDebounce, debounceNum) {

    return (notDebounce === true) || (debounceNum === debouncer);
};

/**
 * Operations to perform after a snippet has been loaded successfully
 * @returns {void}
 * @private
 */
var postSuccess = function postSuccess() {

    if (window.WH && window.WH.messageBus) {
        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_SNIPPET_CHANGED);
    }
};

/**
 * Removes trailing slash from a string if it exists
 * @param str
 * @returns {string}
 */
function stripTrailingSlash(str) {
    return str.endsWith('/') ? str.slice(0, -1) : str;
}

/**
 * Checks whether the navigation occurs to the page driven by NGS
 * @param href page navigating to
 * @returns {boolean} whether the target page is NGS driven
 */
function isNavigatingToNgsPage(href) {
    if (!window.whStatic.config.ngs) return false;
    const {
        enabledDeviceTypes,
        enabledPages,
    } = window.whStatic.config.ngs;
    const normalisedHref = stripTrailingSlash(href.split('?')[0].split('#')[0]);
    
    const decodedHref = decodeURI(normalisedHref);

    const deviceType = DeviceDetector.getDeviceType();
    const deviceTypeEnabled = enabledDeviceTypes.includes(deviceType);


    const pageEnabled = !!enabledPages.find((enabledPage) => {
        const featureString = '~feature:';
        const [regex, featureKey] = enabledPage.split(featureString);

        if(!new RegExp(regex).test(decodedHref)){
            return false;
        }

        if(featureKey){
            return isFeatureEnabled(featureKey); 
        }

        return true;
    });

    return deviceTypeEnabled && pageEnabled;
}

/**
 * Checks whether the feature is enabled or not
 * @param featureKey feature to check
 * @returns {boolean} whether the feature is enabled or not
 */
function isFeatureEnabled(featureKey) {
    const featureCookieName = 'features';
    const separator = ',';
    const featuresCookie = docCookies.getItem(featureCookieName);
    const features = featuresCookie ? featuresCookie.split(separator) : [];
    return features.some(feature => feature === featureKey);
}

/**
 * Function used for navigation where {this} is a jQuery element
 *
 * @param {String|Object} providedHref Href value to navigate or jQuery element with href value
 * @param {Boolean} trigger Flag to trigger page re-render
 * @param {Boolean} ignoreBackButton Flag to ignore to notify backbutton
 * @param {Boolean} replace Flag to replace path in history
 *
 * @returns {Boolean} Returns boolean value from routerNavigate
 *
 * @private
 */
const navigate = function (providedHref, trigger, ignoreBackButton, replace) {
    const href = getNormalisedHref.call(this, providedHref);

    if (!Router.router.started) {
        window.location.replace(Translator.translateUrl(href, 'from'));
    } else if(isNavigatingToNgsPage(href)) {
        window.location.assign(Translator.translateUrl(href, 'from'));
    } else {
        return routerNavigate(href, { trigger, ignoreBackButton, replace });
    }
};

/**
 * Function used for Router navigation
 *
 * @param {String|Object} providedHref Href value to navigate to
 * @param {Boolean} trigger Flag to trigger page re-render
 * @param {Boolean} ignoreBackButton Flag to ignore to notify backbutton
 * @param {Boolean} replace Flag to replace path in history
 *
 * @returns {Boolean} Returns false to stop event propagation
 *
 * @private
 */
const routerNavigate = (href, { trigger, ignoreBackButton, replace }) => {
    if(!(window.WH.sportsbook.page === 'promotions' && _.includes(window.location.hash, 'offer/'))){
        window.location.hash = '';
    }

    notifyBackButton(href, ignoreBackButton);
    Router.router.navigate(href, { trigger: getTriggerValue(trigger), replace });
    return false;
};

/**
 * Notifies BackButton about new path trigger
 *
 * @param path
 * @param ignore
 *
 * @returns {Boolean} Returns trigger value or true if undefined
 *
 * @private
 */
var notifyBackButton = function (path, ignore) {

    // Notify BackButton about the changes
    if (!ignore && path !== window.location.pathname) {

        pubSub.publish(MessageBusTopics. BACK_BUTTON_HISTORY, -1);
    }
};

/**
 * Gets trigger value, if it's undefined it returns default true value
 *
 * @param {Boolean} trigger Flag to trigger new page load
 *
 * @returns {Boolean} Returns trigger value or true if undefined
 *
 * @private
 */
const getTriggerValue = trigger => _.isUndefined(trigger) ? true : trigger;

/**
 * Scrolls the page to the top
 *
 * @returns {void}
 *
 * @private
 */
var scrollPageToTop = function () {

    var heightScreen = jQuery(window).height();
    var positionScroll;

    jQuery(window).resize(function () {
        heightScreen = jQuery(window).height();
    });

    jQuery(window).scroll(_.debounce(function pageScroll() {
        positionScroll = jQuery(this).scrollTop();
        if (positionScroll > heightScreen) {
            jQuery('.button__back-to-top.watch-screen').addClass('-is-visible');
        } else {
            jQuery('.button__back-to-top.watch-screen').removeClass('-is-visible');
        }
    }, 200));
};

/**
 * Init buttons to scroll to top
 *
 * @returns {void}
 *
 * @private
 */
var initButtonsScrollToTop = function () {

    jQuery('body')
        .off('click.backtotop')
        .on('click.backtotop', '.button__back-to-top', function () {

            jQuery('html, body, #main-container, .off-canvas__main').animate({ scrollTop: 0 }, '250');
        });
};

/**
 * Applies the magnific popup plugin, which
 * is used in the v1 for the Join popup
 *
 * @returns {void}
 *
 * @private
 */
var bindPopup = function () {

    var setup = function () {
        var self = this;

        window.WH.messageBus.subscribe(MessageBusTopics.DEFERRED_LOAD_MAGNIFIC_POPUP, function () {
            jQuery(self).magnificPopup({
                type: 'iframe',
                closeOnBgClick: false,
                mainClass: jQuery(self).attr('data-main-class'),
                callbacks: {
                    //check if this is needed
                    open: BindingComponents.Login.adjustHeightWidth,
                    change: function () {
                        if (checkOverlayDeviceDetectionIsSet()) {
                            window.MyAccount.overlaysHandler.updateMfpContentId(this);
                            window.MyAccount.overlaysHandler.changeMfpWrapDeviceDetectedClassName(self);
                            window.MyAccount.overlaysHandler
                                .setScrollingOnBody(true);
                        } else {
                            this.content.find('iframe.mfp-iframe')
                                .attr('id', this.currItem.el.attr('data-iframe-id'));
                        }
                    },
                    close: function () {
                        if (checkOverlayDeviceDetectionIsSet()) {
                            window.MyAccount.overlaysHandler
                                .setScrollingOnBody(false);
                        }
                    },
                },
            });

            var checkOverlayDeviceDetectionIsSet = function () {
                return window.MyAccount !== undefined &&
                    window.MyAccount.overlayDeviceDetection;
            };

        });
    };
    var checkMyaccountOverlaysEnabled = function () {
        return window.MyAccount !== undefined && window.MyAccount.myaccountOverlaysEnabled !== undefined &&
            window.MyAccount.myaccountOverlaysEnabled.toString() === 'true';
    };

    if (!checkMyaccountOverlaysEnabled()) {
        jQuery('[data-mfp-src]').each(setup);
    }
};

/**
 * Init Navigation, login, odds format
 * and other components which any page
 * depends on for basic functionality
 *
 * @returns {void}
 *
 * @private
 */
var initComponents = function () {

    BindingComponents.Navigation.init(jQuery);
    BindingComponents.Login.init(PreferencesProvider, jQuery);
    OddsFormatProvider.init();
    BindingComponents.DropdownAdapter.init(jQuery);
    BindingComponents.SportsBanners.init(jQuery);
    BindingComponents.DrawersAdapter.init(jQuery);
    if (DeviceDetector.isMobile()) {
        BettingTVChannelsProvider.init();
    }
};

/**
 * Make a request based on URL set to reload a snippet
 *
 * @param   {Object} URLSet          URL set
 * @param   {Object} callbackWrapper Object containing callbacks for
 *                                   success, error and pre render cases
 * @param   {Function} render        render callback function
 * @param   {Boolean} notDebounce    The flag which indicates if debounce
 *
 * @returns {void}
 *
 * @private
 */
var request = function (URLSet, callbackWrapper, render, notDebounce) {

    /**
     * The debouncer mechanism compares the request number to the current
     * If it doesn't match, the callback isn't executed.
     */
    debouncer++;
    var debounceNum = debouncer;

    /**
     * Function to be run upon starting
     * the AJAX request
     *
     * @returns {void}
     *
     * @private
     */
    var onStart = function () {

        showPagePreloader();

    };

    /**
     * Function to be run upon the completion
     * of the AJAX request
     *
     * @returns {void}
     *
     * @private
     */
    var onComplete = function () {

        OddsFormatProvider.oddsConvert(false);
        hidePagePreloader();

    };

    /**
     * Request query parameters
     *
     * @type {String}
     */
    //var params = 'snippetReload=true&cache='+parseInt(Math.random()*100000, 10);
    jQuery.ajax({
        url: URLSet.requestURL,
        beforeSend: onStart,
        complete: onComplete,
        data: getFeatureParamsFromCookie(),
        success: function () {

            if (ifDebounce(notDebounce, debounceNum)) {

                render.apply(this, arguments);
            }
        },
        error: function (data, error) {
            if (ifDebounce(notDebounce, debounceNum) && data.getResponseHeader('content-type') === 'text/html') {

                render.apply(this, [data.responseText, error, data]);
            }
            callbackWrapper.errorCallback(data);
        },
        dataType: 'html',
    });
};

/**
 * Make a request based on URL to get JSON data
 *
 * @param   {Object} url
 * @param   {Object} callbackWrapper Object containing callbacks for
 *                                   success, error and pre render cases
 *
 * @returns {XMLHttpRequest}
 *
 * @private
 */
var requestJSON = function (url, callbackWrapper) {

    /**
     * Request query parameters
     *
     * @type {String}
     */
    //var params = 'snippetReload=true&cache='+parseInt(Math.random()*100000, 10);

    /**
     * Request headers
     *
     * @type {Object}
     */
    var headers = {
        'Cache-Control': 's-maxage=0,max-age=0,public',
    };

    return jQuery
        .ajax({
            url: url,
            headers: headers,
            dataType: 'json',
            data: getFeatureParamsFromCookie(),
        })
        .done(callbackWrapper.successCallback)
        .fail(callbackWrapper.errorCallback)
        .always(callbackWrapper.alwaysCallback);
};

/**
 * Triggers a navigation event using parameters from a messagebus trigger
 *
 * @param {String}  topic   The messagebus topic that triggered this action
 * @param {Object}  params  Params object with url, reRender? and ignoreBackButton?
 *
 * @returns {void}
 *
 * @private
 */
const handleRouteFromMessage = (topic, { url, reRender, ignoreBackButton, replace }) => {
    navigate(url, reRender, ignoreBackButton, replace);
};

/**
 * Init operations
 *
 * @returns {void}
 *
 * @private
 */
var initOperationsCommon = function initOperationsCommon() {

    initComponents();
    bindPopup();
    scrollPageToTop();
    initButtonsScrollToTop();
};

/**
 * Init PushAdapter
 *
 * @returns {void}
 *
 * @private
 */
var initPushAdapter = function initPushAdapter() {

    PushAdapter.init({
        navigate: navigate,
    });
};

/**
 * Initialises the internalRoute message listener
 *
 * @returns {void}
 *
 * @private
 */
var initMessageListeners = function initMessageListeners() {

    window.WH.messageBus.subscribe(MessageBusTopics.DO_INTERNAL_ROUTE, handleRouteFromMessage);
    window.WH.messageBus.subscribe(MessageBusTopics.ADD_CAROUSEL_ELEMENT, addCarouselElement);
    window.WH.messageBus.subscribe(MessageBusTopics.ADD_LEFT_NAV_ELEMENT, addLeftNavElement);
};

/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
var api = {

    /**
     * Init function for the current page
     *
     * @returns {void}
     *
     * @private
     */
    init: function () {

        content = jQuery('main div.mask');

        initOperationsCommon();
        initPushAdapter();
        initMessageListeners();
    },

    /**
     * Function used for navigation
     * where {this} is a jQuery element
     *
     * @param   {String}    url         URL
     * @param   {Object}    callbacks   Success and error callbacks
     *
     * @returns {Boolean} Returns false if a router
     *                            has been already started
     *
     * @public
     */
    navigate: navigate,

    /**
     * Changes the current window location properly
     *
     * @param {String} URL Href value
     *
     * @public
     */
    replaceLocationURL: function replaceLocationURL(URL) {

        var href = getNormalisedHref.call(this, URL);
        window.location.replace(Translator.translateUrl(href, 'from'));
    },

    /**
     * Function to obtain JSON resource
     *
     * @param   {String} url
     * @param   {Object} callbacks
     *
     * @returns {XMLHttpRequest}
     *
     * @public
     */
    getJSONResource: function (url, callbacks) {

        url = addAjaxDomain(url, window.WH.sportsbook);

        /**
         * Object containing callbacks
         *
         * @type {Object}
         */
        var callbackWrapper = {

            /**
             * Actions to perform on success.
             *
             * @returns {void}
             *
             * @private
             */
            successCallback: function (data, status, xhr) {

                if (callbacks && callbacks.afterSuccess !== undefined) {

                    window.WH.messageBus.publish(MessageBusTopics.GET_JSON_RESOURCE_COMPLETED);
                    callbacks.afterSuccess(data, status, xhr);
                }
            },

            /**
             * Actions to perform on error.
             *
             * @returns {void}
             *
             * @private
             */
            errorCallback: function (xhr, status, error) {

                if (callbacks && callbacks.afterError !== undefined) {
                    callbacks.afterError(xhr, status, error);
                }
            },

            /**
             * Always called after the action.
             *
             * @returns {void}
             *
             * @private
             */
            alwaysCallback: function (data, status) {

                OddsFormatProvider.oddsConvert(false);
                if (callbacks && callbacks.always !== undefined) {
                    callbacks.always(data, status);
                }
            },
        };

        return requestJSON(url, callbackWrapper);
    },

    /**
     * Makes an Ajax requests and re render the content of a certain DOM element.
     *
     * @param {Object}  URLSet                URLs to be requested and possible redirections
     * @param {Object}  URLSet.requestURL
     * @param {Object}  URLSet.redirectURL
     * @param {Object}  URLSet.replaceHistory If true actual url is not appended to history stack, but replaces last
     * @param {String}  selector              DOM Container selector
     * @param {Object}  callbacks             Success and error callbacks
     * @param {Boolean} notDebounce           The flag which indicates if debounce
     * @param {Boolean} append                Whether to replace the DOM container
     *
     * @returns {void}
     */
    reloadSnippet: function (URLSet, selector, callbacks, notDebounce, append, useCdn) {

        var container = jQuery(document.querySelector(selector));

        URLSet.requestURL = addDomain(URLSet.requestURL, window.WH.sportsbook, useCdn);

        URLSet.requestURL = Translator.addTranslationTipToURL(URLSet.requestURL);

        var callbackWrapper = {

            /**
             * Actions to perform on success.
             *
             * @returns {void}
             *
             * @private
             */
            successCallback: function (container) {

                if (callbacks && callbacks.afterSuccess !== undefined) {

                    callbacks.afterSuccess();

                    SportsDataRepository.indexDom(container);

                    postSuccess();
                }
            },

            /**
             * Actions to perform on error.
             *
             * @returns {void}
             *
             * @private
             */
            errorCallback: function (error) {

                if (callbacks && callbacks.afterError !== undefined) {
                    callbacks.afterError(error);
                }
            },

            /**
             * Allows us to modify the incoming data before it's
             * rendered in the page
             *
             * @param  {Object} data Data to process
             *
             * @return {Object}      Processed data
             */
            preRenderCallback: function (data) {

                if (callbacks && callbacks.preRender !== undefined) {
                    return callbacks.preRender(data);
                } else {
                    return data;
                }
            },
        };

        /**
         * Renders the snippet after the Ajax request is completed
         *
         * @param   {String} data Ajax Response
         *
         * @returns {void}
         */
        var render = function (data) {

            var changeURL = function () {

                if (URLSet.redirectURL !== undefined) {
                    // run hooks
                    Router.hooks.exec();
                    // Notify BackButton about the changes
                    notifyBackButton(URLSet.redirectURL);
                    // Change URL and history
                    Router.router.navigate(URLSet.redirectURL, {
                        trigger: false,
                        replace: !!URLSet.replaceHistory,
                    });
                }
            };

            changeURL();

            var processedData = callbackWrapper.preRenderCallback(data);

            if (append) {

                container.append(processedData);
            } else {

                container.html(processedData);
            }

            if (DeviceDetector.isNative()) {
                jQuery('.play-in-racecard').addClass('displayNone');
                jQuery('.click-to-watch').removeClass( 'hide-on-mobile' );
                jQuery('.click-to-watch').addClass('displayNone');
                jQuery('.click-to-watch-native').removeClass('displayNone');
                jQuery('.watch-text').removeClass( 'show-on-mobile-upper' );
                jQuery('.play-in-racecard').addClass( 'displayNone' );
                jQuery('.play-in-natives').removeClass( 'displayNone' );
                jQuery('.play-in-non-racecard').addClass( 'displayNone' );
                jQuery('.betting-text-icon').addClass( 'displayNone' );

                if (!isNativeSupportForNewBettingTv()) {
                    jQuery('.new-native').addClass('displayNone');
                    jQuery('.legacy-native').removeClass('displayNone');
                }
            }

            callbackWrapper.successCallback(container);
        };

        request(URLSet, callbackWrapper, render, notDebounce);
    },

    /**
     * Return page content
     *
     * @returns {Object} jQuery element
     *
     * @public
     */
    getContent: function () {

        return content;
    },
    isNativeSupportForNewBettingTv: isNativeSupportForNewBettingTv,

    oddsConvert: OddsFormatProvider.oddsConvert,
    LazyLoadingViewAdapter: LazyLoadingViewAdapter,
    PushAdapter: PushAdapter,
    DomPushAdapter: DomPushAdapter,
    IntegrationGlobals: IntegrationGlobals,
    internals: {
        utilities: {
            normaliseHref: normaliseHref,
        },
    },
};

export default api;
